<script>
  import { tick } from 'svelte';
  import { routeStore } from './lib/stores/routeStore.js';
  import { geocodeBatch } from './lib/utils/postcodeGeocoder.js';
  import { calculateMatrix } from './lib/utils/distanceCalculator.js';
  import { optimize } from './lib/utils/routeOptimizer.js';
  import Landing from './routes/Landing.svelte';
  import RouteInput from './routes/RouteInput.svelte';
  import RouteNavigation from './routes/RouteNavigation.svelte';

  let currentView = 'landing'; // landing, input, navigation
  let isOptimizing = false;
  let optimizationError = null;

  function handleStart() {
    currentView = 'input';
  }

  function handleBackToHome() {
    currentView = 'landing';
  }

  async function handleOptimize() {
    isOptimizing = true;
    optimizationError = null;

    try {
      const route = $routeStore;

      // Step 1: Geocode all postcodes to get coordinates
      const geocodedStops = await geocodeBatch(route.postcodes);
      const stopCoordinates = geocodedStops.map(g => ({ lat: g.lat, lng: g.lng }));

      // Step 2: Get starting location coordinate
      let startCoordinate;
      if (route.startLocation.lat && route.startLocation.lng) {
        // Already a coordinate (from GPS)
        startCoordinate = {
          lat: route.startLocation.lat,
          lng: route.startLocation.lng
        };
      } else {
        // Need to geocode the starting postcode
        const { geocode } = await import('./lib/utils/postcodeGeocoder.js');
        const geocoded = await geocode(route.startLocation.postcode);
        startCoordinate = { lat: geocoded.lat, lng: geocoded.lng };
      }

      // Step 3: Calculate distance and time matrices
      // Combine start + stops for distance calculation
      const allCoordinates = [startCoordinate, ...stopCoordinates];
      const { distanceMatrix, timeMatrix } = await calculateMatrix(allCoordinates);

      // Step 4: Run route optimization
      // The optimize function returns indices for stops only (excluding start)
      const optimizedStopIndices = optimize(stopCoordinates, startCoordinate, distanceMatrix);

      // Step 5: Store the optimized route
      routeStore.setOptimizedRoute({
        coordinates: stopCoordinates,
        optimizedSequence: optimizedStopIndices,
        distanceMatrix: distanceMatrix.slice(1).map(row => row.slice(1)), // Remove start location from matrix
        timeMatrix: timeMatrix.slice(1).map(row => row.slice(1)) // Remove start location from matrix
      });

      currentView = 'navigation';
    } catch (error) {
      console.error('Route optimization failed:', error);
      optimizationError = error.message;
      alert(`Failed to optimize route: ${error.message}`);
    } finally {
      isOptimizing = false;
    }
  }

  async function handleNewRoute() {
    routeStore.reset();
    await tick(); // Wait for all pending state changes
    currentView = 'landing';
  }
</script>

<main class="min-h-screen">
  {#if currentView === 'landing'}
    <Landing onStart={handleStart} />
  {:else if currentView === 'input'}
    <RouteInput onOptimize={handleOptimize} {isOptimizing} onBackToHome={handleBackToHome} />
  {:else if currentView === 'navigation'}
    <RouteNavigation onNewRoute={handleNewRoute} onBackToHome={handleBackToHome} />
  {/if}
</main>
